







































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import moment, { Moment } from 'moment';

export interface MaintenanceCalendarEvent {
  id: string;
  stationId: string;
  customerId: string;
  title: string;
  subtitle: string;
  date: string;
  isOverdue: boolean;
  actuatorCount?: number;
}

interface TimelineMonth {
  key: string;
  label: string;
}

interface DisplayEvent extends MaintenanceCalendarEvent {
  dateLabel: string;
  monthKey: string;
}

const VISIBLE_EVENTS = 10;
const MONTHS_PER_VIEW = 3;
const GERMAN_MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

@Component({})
export default class MaintenanceCalendarTimelineComponent extends Vue {
  @Prop({ default: () => [] }) events!: MaintenanceCalendarEvent[];
  @Prop({ default: 3 }) months!: number;
  @Prop({ default: false }) loading!: boolean;
  @Prop({ default: 'Keine anstehenden Wartungen im gewählten Zeitraum' }) emptyText!: string;

  public currentMonthPage = 1;
  public expandedMonths: Record<string, boolean> = {};

  private get rangeStart(): Moment {
    return moment().startOf('month');
  }

  private get rangeEnd(): Moment {
    return moment().add(this.months, 'months').endOf('month');
  }

  public get allTimelineMonths(): TimelineMonth[] {
    const months: TimelineMonth[] = [];
    const cursor = this.rangeStart.clone().startOf('month');
    const end = this.rangeEnd.clone();

    while (cursor.isSameOrBefore(end, 'month')) {
      months.push({
        key: cursor.format('YYYY-MM'),
        label: `${GERMAN_MONTHS[cursor.month()]} ${cursor.format('YYYY')}`,
      });
      cursor.add(1, 'month').startOf('month');
    }

    return months;
  }

  public get totalMonthPages(): number {
    return Math.max(1, Math.ceil(this.allTimelineMonths.length / MONTHS_PER_VIEW));
  }

  public get visibleTimelineMonths(): TimelineMonth[] {
    const start = (this.currentMonthPage - 1) * MONTHS_PER_VIEW;
    return this.allTimelineMonths.slice(start, start + MONTHS_PER_VIEW);
  }

  public get monthPageLabel(): string {
    const first = this.visibleTimelineMonths[0]?.label ?? '';
    const last = this.visibleTimelineMonths[this.visibleTimelineMonths.length - 1]?.label ?? '';
    if (first && last && first !== last) {
      return `${first} – ${last}`;
    }
    return `Seite ${this.currentMonthPage} / ${this.totalMonthPages}`;
  }

  public get displayEvents(): DisplayEvent[] {
    const monthKeys = new Set(this.allTimelineMonths.map((month) => month.key));
    const firstMonthKey = this.allTimelineMonths[0]?.key ?? '';

    return [...this.events]
      .sort((a, b) => moment(a.date, 'YYYY-MM-DD').valueOf() - moment(b.date, 'YYYY-MM-DD').valueOf())
      .map((event) => {
        let monthKey = moment(event.date, 'YYYY-MM-DD').format('YYYY-MM');
        if (!monthKeys.has(monthKey)) {
          monthKey = moment(event.date, 'YYYY-MM-DD').isBefore(this.rangeStart, 'month')
            ? firstMonthKey
            : this.allTimelineMonths[this.allTimelineMonths.length - 1]?.key ?? monthKey;
        }

        return {
          ...event,
          monthKey,
          dateLabel: moment(event.date, 'YYYY-MM-DD').format('DD.MM.YYYY'),
        };
      });
  }

  public get eventsByMonth(): Record<string, DisplayEvent[]> {
    const grouped: Record<string, DisplayEvent[]> = {};

    for (const month of this.allTimelineMonths) {
      grouped[month.key] = [];
    }

    for (const event of this.displayEvents) {
      if (grouped[event.monthKey]) {
        grouped[event.monthKey].push(event);
      }
    }

    return grouped;
  }

  public allEventsForMonth(monthKey: string): DisplayEvent[] {
    return this.eventsByMonth[monthKey] ?? [];
  }

  public displayedEventsForMonth(monthKey: string): DisplayEvent[] {
    const all = this.allEventsForMonth(monthKey);
    if (this.isMonthExpanded(monthKey)) return all;
    return all.slice(0, VISIBLE_EVENTS);
  }

  public collapsedCountForMonth(monthKey: string): number {
    return Math.max(0, this.allEventsForMonth(monthKey).length - VISIBLE_EVENTS);
  }

  public isMonthExpanded(monthKey: string): boolean {
    return !!this.expandedMonths[monthKey];
  }

  public toggleMonthExpanded(monthKey: string): void {
    this.$set(this.expandedMonths, monthKey, !this.expandedMonths[monthKey]);
  }

  public collapseLabel(monthKey: string): string {
    const hidden = this.collapsedCountForMonth(monthKey);
    if (this.isMonthExpanded(monthKey)) return 'Weniger anzeigen';
    return `${hidden} weitere anzeigen`;
  }

  @Watch('months')
  private onMonthsChange() {
    this.currentMonthPage = 1;
    this.expandedMonths = {};
  }

  @Watch('events')
  private onEventsChange() {
    if (this.currentMonthPage > this.totalMonthPages) {
      this.currentMonthPage = this.totalMonthPages;
    }
  }

  @Watch('currentMonthPage')
  private onMonthPageChange() {
    this.expandedMonths = {};
  }

  public eventRoute(event: MaintenanceCalendarEvent) {
    return {
      name: 'station_details_view',
      params: {
        customerId: event.customerId,
        stationId: event.stationId,
      },
    };
  }
}
